import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useUnit } from "effector-react";
import { UnifiedDialog } from "../../shared/ui";
import { createArticleModel, InputArticle } from "./model";
import { DatePickerInput } from "@mantine/dates";
import { IconTrash } from "@tabler/icons-react";
import { CsvFileInput } from "./csv-file-input";
import { useEffect, useState } from "react";
import { isValidUrl } from "../../entities/articles/utils/is-valid-url";
import { notifications } from "@mantine/notifications";

export const ProcessArticle = ({
  isLoading = false,
}: {
  isLoading?: boolean;
}) => {
  const [inputArticle, setInputArticle] = useState<InputArticle | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    opened,
    open,
    close,
    addedArticles,
    reset,
    create,
    addArticles,
    removeArticle,
  } = useUnit({
    opened: createArticleModel.$opened,
    open: createArticleModel.open,
    close: createArticleModel.close,
    addedArticles: createArticleModel.$addedArticles,
    reset: createArticleModel.reset,
    create: createArticleModel.create,
    addArticles: createArticleModel.addArticles,
    removeArticle: createArticleModel.removeArticle,
  });

  const [isPending] = useUnit([createArticleModel.createFx.pending]);

  const handleCreate = () => {
    const inputIsValid = inputArticle?.url && isValidUrl(inputArticle?.url);

    if (inputIsValid) {
      addArticles([
        ...addedArticles,
        { url: inputArticle?.url, date: inputArticle?.date, skipCutLongSentences: inputArticle?.skipCutLongSentences },
      ]);
    }

    const inputExistButNotValid =
      inputArticle?.url && !isValidUrl(inputArticle?.url);

    if (inputExistButNotValid) {
      notifications.show({
        message: "Invalid URL",
        color: "red",
      });
      return;
    }

    create();
  };

  useEffect(() => {
    if (inputArticle) {
      setError(isValidUrl(inputArticle?.url) ? null : "Invalid URL");
    }
  }, [inputArticle]);

  useEffect(() => {
    setInputArticle(null);
    setError(null);
  }, [opened]);

  return (
    <>
      <UnifiedDialog
        opened={opened}
        onClose={close}
        loading={isPending}
        okText="Submit"
        onOk={handleCreate}
        title="Add articles for processing"
        body={
          <Stack>
            <TextInput
              value={inputArticle?.url ?? ""}
              onChange={(e) => {
                setInputArticle({
                  url: e.currentTarget.value,
                  date: inputArticle?.date,
                  skipCutLongSentences: inputArticle?.skipCutLongSentences ?? false,
                });
              }}
              label="Enter article URL"
              placeholder="https://example.com/article"
              error={error}
            />

            <DatePickerInput
              value={inputArticle?.date ?? null}
              onChange={(date) =>
                setInputArticle({
                  url: inputArticle?.url ?? "",
                  date: date ? new Date(date) : undefined,
                  skipCutLongSentences: inputArticle?.skipCutLongSentences ?? false,
                })
              }
              clearable
              highlightToday
              label="Select article date"
              placeholder="Select date"
            />

            <Checkbox
              label="Skip cutting the long sentences"
              onChange={(e) =>
                setInputArticle({
                  url: inputArticle?.url ?? "",
                  date: inputArticle?.date,
                  skipCutLongSentences: e.currentTarget.checked,
                })
              }
            />

            <Button
              variant="default"
              onClick={() => {
                if (inputArticle && isValidUrl(inputArticle.url)) {
                  addArticles([inputArticle]);
                  setInputArticle(null);
                }
              }}
            >
              + Add article
            </Button>

            <Text size="sm" c="dimmed" inline mt={7} ta="center">
              Or
            </Text>

            <CsvFileInput onUpdate={addArticles} />

            {addedArticles.length > 0 && (
              <Stack gap={"sm"}>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Tasks ({addedArticles.length})
                  </Text>
                  <Text
                    size="sm"
                    fw={500}
                    onClick={() => reset()}
                    style={{ cursor: "pointer" }}
                  >
                    Remove all
                  </Text>
                </Group>
                <Stack gap={"xs"}>
                  {addedArticles.map((article, index) => (
                    <Group
                      key={article.url}
                      wrap="nowrap"
                      justify="space-between"
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "10px",
                        borderRadius: "5px",
                      }}
                    >
                      <Text truncate size="sm">
                        {index + 1}. {article.url}
                      </Text>

                      <ActionIcon
                        variant="transparent"
                        size="xs"
                        color="red"
                        w={30}
                        styles={{
                          icon: {
                            justifyContent: "right",
                          },
                        }}
                        onClick={() => removeArticle(article.url)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        }
      />

      <Button bg="green" size="md" onClick={open} disabled={isLoading}>
        Add Task
      </Button>
    </>
  );
};
